import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./testing_tool.css";
import { generateTestingToolReport } from "./testing_tool_api_service";
import { getAuditSlugFromResponse } from "./testing_tool_report_utils";

const AUDIT_GOAL_OPTIONS = [
  "Friction",
  "Clarity",
  "Conversion",
  "Accessibility",
  "Trust",
];

const defaultForm = {
  url: "",
  goal: "",
  model: "openrouter/auto",
  useCompany: true,
  topK: 7,
};

export default function TestingTool() {
  const navigate = useNavigate();
  const [form, setForm] = useState(defaultForm);
  const [auditGoals, setAuditGoals] = useState([]);
  const [goalMenuOpen, setGoalMenuOpen] = useState(false);
  const [screenshots, setScreenshots] = useState([]);
  const [uploadError, setUploadError] = useState("");
  const [isDragActive, setIsDragActive] = useState(false);
  const [urlError, setUrlError] = useState("");
  const [goalError, setGoalError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const multiselectRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    function handlePointerDown(event) {
      if (!multiselectRef.current?.contains(event.target)) {
        setGoalMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, []);

  useEffect(() => {
    return () => {
      screenshots.forEach((item) => {
        if (item.previewUrl) {
          URL.revokeObjectURL(item.previewUrl);
        }
      });
    };
  }, [screenshots]);

  function toggleGoal(value) {
    setAuditGoals((prev) =>
      prev.includes(value) ? prev.filter((g) => g !== value) : [...prev, value]
    );
  }

  const goalSummary =
    auditGoals.length === 0 ? "Select your audit goal" : auditGoals.join(", ");

  const isValidUrl = (value) => {
    if (!value || !value.trim()) return false;
    try {
      const parsed = new URL(value.trim());
      return parsed.protocol === "http:" || parsed.protocol === "https:";
    } catch {
      return false;
    }
  };

  const handleSubmit = async () => {
    if (isSubmitDisabled || isSubmitting) return;
    let hasError = false;

    if (!isValidUrl(form.url)) {
      setUrlError("Please enter a valid website URL.");
      hasError = true;
    } else {
      setUrlError("");
    }

    if (!form.goal.trim()) {
      setGoalError("Please add your audit goal.");
      hasError = true;
    } else {
      setGoalError("");
    }

    if (hasError) return;

    const payload = new FormData();
    payload.append("url", form.url);
    payload.append("goal", form.goal);
    payload.append("model", form.model);
    payload.append("topK", String(form.topK));
    payload.append("useCompany", String(form.useCompany));

    screenshots.forEach((item) => {
      payload.append("screenshots[]", item.file);
    });

    try {
      setIsSubmitting(true);
      const data = await generateTestingToolReport(payload);
      const auditSlug = getAuditSlugFromResponse(data);

      if (auditSlug) {
        navigate(`/testing-tool/${encodeURIComponent(auditSlug)}`);
      } else {
        console.error(
          "Testing tool report generated without a slug; cannot open results page.",
          data
        );
      }
    } catch (error) {
      console.error("Failed to generate testing_tool report", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const attachFiles = (incomingFiles) => {
    const allowedTypes = ["image/png", "image/jpeg", "image/webp"];
    const maxFileSizeInBytes = 5 * 1024 * 1024;
    const nextFiles = [];
    const rejectedReasons = [];

    incomingFiles.forEach((file) => {
      if (!allowedTypes.includes(file.type)) {
        rejectedReasons.push(`${file.name}: unsupported file type`);
        return;
      }
      if (file.size > maxFileSizeInBytes) {
        rejectedReasons.push(`${file.name}: exceeds 5MB`);
        return;
      }
      const duplicate = screenshots.some((existing) => {
        const existingFile = existing.file;
        return (
          existingFile.name === file.name &&
          existingFile.size === file.size &&
          existingFile.lastModified === file.lastModified
        );
      });
      if (duplicate) {
        return;
      }
      nextFiles.push({
        file,
        previewUrl: URL.createObjectURL(file),
      });
    });

    if (nextFiles.length > 0) {
      setScreenshots((prev) => [...prev, ...nextFiles]);
      setUploadError("");
    } else if (rejectedReasons.length > 0) {
      setUploadError(rejectedReasons[0]);
    }
  };

  const handleFileChange = (event) => {
    const files = Array.from(event.target.files || []);
    attachFiles(files);
    event.target.value = "";
  };

  const removeScreenshot = (indexToRemove) => {
    setScreenshots((prev) =>
      prev.filter((item, index) => {
        const shouldKeep = index !== indexToRemove;
        if (!shouldKeep && item.previewUrl) {
          URL.revokeObjectURL(item.previewUrl);
        }
        return shouldKeep;
      })
    );
  };

  const handleResetDemoValues = () => {
    screenshots.forEach((item) => {
      if (item.previewUrl) {
        URL.revokeObjectURL(item.previewUrl);
      }
    });

    setForm(defaultForm);
    setAuditGoals([]);
    setScreenshots([]);
    setUploadError("");
    setIsDragActive(false);
    setUrlError("");
    setGoalError("");
    setGoalMenuOpen(false);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setIsDragActive(false);
    const files = Array.from(event.dataTransfer.files || []);
    attachFiles(files);
  };

  const isSubmitDisabled = !form.url.trim() || form.goal === "";

  return (
    <div className="testing_tool">
      <header className="testing_tool-header">
        <div className="testing_tool-header-inner">
          <a href="/testing-tool" className="testing_tool-logo">
            <img src="/img/testing-tool/UserQ-logo.png" alt="UserQ" />
          </a>
        </div>
      </header>

      <main className="testing_tool-main">
        <section className="testing_tool-hero">
          <h1 className="testing_tool-title">
            Generate an{" "}
            <span className="testing_tool-title-accent">instant UX audit</span>{" "}
            from any URL
          </h1>

          <p className="testing_tool-lead">
            Get instant, AI-driven insights on friction points, usability,
            accessibility and conversion patterns in seconds.
          </p>

          <div className="testing_tool-steps">
            <div className="testing_tool-step testing_tool-step--active">
              <span className="testing_tool-step-circle">1</span>
              <span className="testing_tool-step-label">Enter URL</span>
            </div>
            <span className="testing_tool-step-divider">—</span>
            <div className="testing_tool-step">
              <span className="testing_tool-step-circle">2</span>
              <span className="testing_tool-step-label">Define goal</span>
            </div>
            <span className="testing_tool-step-divider">—</span>
            <div className="testing_tool-step">
              <span className="testing_tool-step-circle">3</span>
              <span className="testing_tool-step-label">Generate report</span>
            </div>
          </div>
        </section>

        <section className="testing_tool-features">
          <div className="testing_tool-feature-item">
            <img
              src="/img/testing-tool/AccessibilityCheck.svg"
              alt="Accessibility Check"
              className="testing_tool-feature-icon"
            />
            <div className="testing_tool-feature-content">
              <span className="testing_tool-feature-title">
                Accessibility Check
              </span>
              <span className="testing_tool-feature-desc">
                WCAG compliance & contrast ratios
              </span>
            </div>
          </div>
          <div className="testing_tool-feature-item">
            <img
              src="/img/testing-tool/InteractionFlow.svg"
              alt="Interaction Flow"
              className="testing_tool-feature-icon"
            />
            <div className="testing_tool-feature-content">
              <span className="testing_tool-feature-title">
                Interaction Flow
              </span>
              <span className="testing_tool-feature-desc">
                Feedback & navigation friction
              </span>
            </div>
          </div>
          <div className="testing_tool-feature-item">
            <img
              src="/img/testing-tool/VisualHierarchy.svg"
              alt="Visual Hierarchy"
              className="testing_tool-feature-icon"
            />
            <div className="testing_tool-feature-content">
              <span className="testing_tool-feature-title">
                Visual Hierarchy
              </span>
              <span className="testing_tool-feature-desc">
                Scanning patterns & layout flow
              </span>
            </div>
          </div>
          <div className="testing_tool-feature-item">
            <img
              src="/img/testing-tool/QuickWins.svg"
              alt="Quick Wins"
              className="testing_tool-feature-icon"
            />
            <div className="testing_tool-feature-content">
              <span className="testing_tool-feature-title">Quick Wins</span>
              <span className="testing_tool-feature-desc">
                Low effort, high impact fixes
              </span>
            </div>
          </div>
        </section>

        <section className="testing_tool-card">
          <div className="testing_tool-card-menu">
            <p className="testing_tool-card-kicker">Configure Audit</p>
            <p
              className="testing_tool-card-menu-reset"
              onClick={handleResetDemoValues}
            >
              Reset to demo values
            </p>
          </div>

          <div className="testing_tool-form-stack">
            <div className="testing_tool-url-field">
              <label
                className="testing_tool-field-label"
                htmlFor="testing-tool-url"
              >
                Share the website URL
              </label>
              <input
                id="testing-tool-url"
                type="url"
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck={false}
                value={form.url}
                onChange={(e) => {
                  setForm({ ...form, url: e.target.value });
                  if (urlError) setUrlError("");
                }}
                placeholder="https://www.example.com"
                className="testing_tool-input"
              />
              {form.url.trim() ? (
                <img
                  src="/img/testing-tool/urlIcon.svg"
                  alt=""
                  aria-hidden
                  className="testing_tool-url-icon"
                />
              ) : null}
              {urlError ? (
                <p className="testing_tool-upload-error">{urlError}</p>
              ) : null}
              <div className="testing_tool-textarea-footer">
                <p className="testing_tool-helper">
                  Our AI automatically identifies your site type (e.g.
                  E-commerce, SaaS) to tailor the audit goals.
                </p>
              </div>
            </div>

            <div>
              <span className="testing_tool-field-label" id="audit-goal-label">
                Tell us your audit goal
              </span>
              <div
                ref={multiselectRef}
                className={`testing_tool-multiselect${goalMenuOpen ? " is-open" : ""}${auditGoals.length > 0 ? " is-selected" : ""}`}
              >
                <button
                  type="button"
                  className="testing_tool-multiselect-trigger"
                  aria-haspopup="listbox"
                  aria-expanded={goalMenuOpen}
                  aria-labelledby="audit-goal-label"
                  onClick={() => setGoalMenuOpen((o) => !o)}
                >
                  <span
                    className={`testing_tool-multiselect-trigger-label${auditGoals.length === 0 ? " is-placeholder" : ""}`}
                  >
                    {goalSummary}
                  </span>
                  <span className="testing_tool-multiselect-chevron" aria-hidden>
                    <img
                      src="/img/testing-tool/arrow-down-round.svg"
                      alt=""
                      className="testing_tool-multiselect-chevron-icon"
                    />
                  </span>
                </button>
                {goalMenuOpen && (
                  <div
                    className="testing_tool-multiselect-menu"
                    role="listbox"
                    aria-multiselectable="true"
                  >
                    {AUDIT_GOAL_OPTIONS.map((option) => {
                      const checked = auditGoals.includes(option);
                      return (
                        <label
                          key={option}
                          className="testing_tool-multiselect-option"
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleGoal(option)}
                          />
                          <span className="testing_tool-multiselect-box">
                            <span className="testing_tool-multiselect-check" />
                          </span>
                          {option}
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            <div>
              <textarea
                id="testing-tool-desc"
                rows={4}
                maxLength={300}
                value={form.goal}
                onChange={(e) => {
                  setForm({ ...form, goal: e.target.value });
                  if (goalError) setGoalError("");
                }}
                placeholder="Tell us more..."
                className="testing_tool-textarea"
                aria-label="Tell us more about your audit"
              />
              {goalError ? (
                <p className="testing_tool-upload-error">{goalError}</p>
              ) : null}
              <div className="testing_tool-textarea-footer">
                <p className="testing_tool-helper">
                  Be as detailed as possible. This drives the AI&apos;s focus.
                </p>
                <p>{form.goal.length}/300 characters</p>
              </div>
            </div>

            <div>
              <span className="testing_tool-upload-label testing_tool-field-label">
                Add screens that help illustrate your goal (optional)
              </span>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                multiple
                className="testing_tool-file-input"
                tabIndex={-1}
                aria-hidden
                onChange={handleFileChange}
              />
              <div
                className={`testing_tool-upload-zone${isDragActive ? " is-drag-active" : ""}`}
                onDragOver={(event) => {
                  event.preventDefault();
                  setIsDragActive(true);
                }}
                onDragLeave={() => setIsDragActive(false)}
                onDrop={handleDrop}
              >
                <div className="testing_tool-upload-icon-wrap">
                  <img
                    src="/img/testing-tool/image-upload.svg"
                    alt=""
                    className="testing_tool-upload-icon"
                  />
                </div>
                <p className="testing_tool-upload-title">
                  Click to upload or drag and drop (PNG, JPG or WebP)
                </p>
                <p className="testing_tool-upload-desc">
                  Provide screenshots of the pages or flows you want to audit.
                </p>
                <button
                  type="button"
                  className="testing_tool-browse"
                  onClick={() => fileInputRef.current?.click()}
                >
                  Browse files
                </button>
              </div>
              {uploadError ? (
                <p className="testing_tool-upload-error">{uploadError}</p>
              ) : null}
              {screenshots.length > 0 ? (
                <div className="testing_tool-upload-list">
                  {screenshots.map((item, index) => (
                    <div
                      key={`${item.file.name}-${item.file.size}-${index}`}
                      className="testing_tool-upload-item"
                    >
                      <img
                        src={item.previewUrl}
                        alt={item.file.name}
                        className="testing_tool-upload-thumb"
                      />
                      <div className="testing_tool-upload-meta">
                        <p className="testing_tool-upload-file-name">
                          {item.file.name}
                        </p>
                        <p className="testing_tool-upload-file-size">
                          {(item.file.size / 1024 / 1024).toFixed(2)} MB
                        </p>
                      </div>
                      <button
                        type="button"
                        className="testing_tool-upload-remove"
                        onClick={() => removeScreenshot(index)}
                      >
                        <img src="/img/testing-tool/XCircle.svg" alt="Remove" />
                      </button>
                    </div>
                  ))}
                </div>
              ) : null}
            </div>

            <button
              type="button"
              className={`testing_tool-submit${isSubmitDisabled ? "" : " is-enabled"}${isSubmitting ? " is-loading" : ""}`}
              onClick={handleSubmit}
              disabled={isSubmitDisabled || isSubmitting}
            >
              {isSubmitting ? (
                <img
                  src="/img/testing-tool/SpinnerGap.svg"
                  alt=""
                  aria-hidden
                  className="testing_tool-submit-icon testing_tool-submit-icon--spinner"
                />
              ) : (
                <>
                  <img
                    src="/img/testing-tool/ai-magic.svg"
                    alt=""
                    className="testing_tool-submit-icon"
                  />
                  Generate UX audit report
                </>
              )}
            </button>

            <div className="testing_tool-features">
              <button
                type="button"
                onClick={() =>
                  setForm((prev) => ({
                    ...prev,
                    url: "https://in.bookmyshow.com/",
                    goal: "Improve booking conversion and reduce drop-offs",
                  }))
                }
                className="testing-tool-results-btn"
              >
                BookMyShow sample
              </button>
              <button
                type="button"
                onClick={() =>
                  setForm((prev) => ({
                    ...prev,
                    url: "https://www.poplify.com/",
                    goal: "Improve checkout conversion and reduce drop-offs",
                  }))
                }
                className="testing-tool-results-btn"
              >
                Poplify sample
              </button>
              <button
                type="button"
                onClick={() =>
                  setForm((prev) => ({
                    ...prev,
                    url: "https://www.example.com/",
                    goal: "Understand friction on a generic e-commerce flow",
                  }))
                }
                className="testing-tool-results-btn"
              >
                Generic sample
              </button>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}
